import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import PasswordInput from './ui/PasswordInput';
import { toast } from 'sonner';

const LoginPage = () => {
  const [formData, setFormData] = useState(() => {
    // Prefill the last successfully-used email so returning users on trusted
    // devices just type the password. Cleared when the user logs out.
    let lastEmail = '';
    try {
      lastEmail = localStorage.getItem('last_login_email') || '';
    } catch {
      /* ignore storage errors */
    }
    return { email: lastEmail, password: '' };
  });
  const [rememberMe, setRememberMe] = useState(() => {
    // Persist "Beni hatırla" preference across sessions so trusted-device
    // users don't have to re-check it every time they land on /login.
    try {
      return localStorage.getItem('remember_me_pref') === '1';
    } catch {
      return false;
    }
  });

  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
    try {
      if (checked) {
        localStorage.setItem('remember_me_pref', '1');
      } else {
        localStorage.removeItem('remember_me_pref');
      }
    } catch {
      /* ignore storage errors (private mode, quota, etc.) */
    }
  };
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: ''
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
        general: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Clear previous errors
    setErrors({ email: '', password: '', general: '' });

    // Basic validation
    let hasError = false;
    const newErrors = { email: '', password: '', general: '' };

    if (!formData.email.trim()) {
      newErrors.email = 'E-posta adresi gerekli';
      hasError = true;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi girin';
      hasError = true;
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Şifre gerekli';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const result = await login(formData.email, formData.password, rememberMe);
      
      if (result.success) {
        // Persist the email so the next visit to /login prefills it.
        try {
          localStorage.setItem('last_login_email', formData.email);
        } catch {
          /* ignore storage errors */
        }
        // If password must be changed, don't navigate yet
        // Modal will open and after password change, user will be redirected
        if (result.must_change_password) {
          toast.info('Lütfen şifrenizi değiştirin');
          // Stay on login page, modal will open
          return;
        }

        // If trial+subscription expired, force user to pick a paid plan
        if (result.trial_expired) {
          toast.info('Deneme süreniz sona ermiştir. Lütfen bir abonelik planı seçin.');
          navigate(result.redirect_to || '/subscription');
          return;
        }

        toast.success('Giriş başarılı! Yönlendiriliyorsunuz...');
        navigate('/dashboard');
      } else if (result.error) {
        const { message, field } = result.error;
        
        if (field === 'email') {
          setErrors(prev => ({ ...prev, email: message }));
        } else if (field === 'password') {
          setErrors(prev => ({ ...prev, password: message }));
        } else {
          setErrors(prev => ({ ...prev, general: message }));
          toast.error(message);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors(prev => ({ ...prev, general: 'Giriş sırasında bir hata oluştu' }));
      toast.error('Giriş sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold gradient-text" data-testid="login-logo">
              Advancecash.io
            </h1>
          </Link>
          <h2 className="mt-4 text-3xl font-bold text-gray-900">
            Hesabınıza Giriş Yapın
          </h2>
          <p className="mt-2 text-gray-600">
            Avans yönetim sisteminize erişin
          </p>
        </div>

        {/* Login Form */}
        <Card className="p-8 shadow-xl border-0">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="form-label">
                E-posta Adresi
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="ornek@sirket.com"
                required
                data-testid="email-input"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600" data-testid="email-error">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="form-label">
                Şifre
              </Label>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-600" data-testid="password-error">
                  {errors.password}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => handleRememberMeChange(e.target.checked)}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded cursor-pointer"
                  data-testid="remember-me-checkbox"
                />
                <Label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer select-none">
                  Beni bu cihazda hatırla (30 gün)
                </Label>
              </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-teal-600 hover:text-teal-500">
                  Şifrenizi mi unuttunuz?
                </Link>
              </div>
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs text-red-600 text-center" data-testid="general-error">
                  {errors.general}
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex justify-center py-3"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <span>Giriş Yap</span>
              )}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Veya</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Henüz hesabınız yok mu?{' '}
                <Link to="/register" className="font-medium text-teal-600 hover:text-teal-500" data-testid="register-link">
                  Hemen Başlayın!
                </Link>
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;